/**
 * Post-render fixups for mini-lit's <markdown-block> (issue #8).
 *
 * We patch the element's `updated()` lifecycle hook instead of forking the
 * component: markdown-block renders into light DOM, so after each render we
 * can post-process the produced HTML in place. Two fixups:
 *
 * 1. Literal <br> in table cells. Qwen likes to emit `<br>` inside GFM table
 *    cells (the only way to get a line break there). MarkdownBlock escapes
 *    all inline HTML for safety, so users see a literal "<br>" glyph. We
 *    convert *only* escaped <br> tags back into real line breaks — no other
 *    HTML is un-escaped, so the safety property is preserved.
 *
 * 2. A hover "copy" button on every rendered table that puts a styled HTML
 *    table (plus a TSV text/plain fallback) on the clipboard, so pasting
 *    into Word / Outlook / Excel keeps the table structure.
 */

import { MarkdownBlock } from "@mariozechner/mini-lit/dist/MarkdownBlock.js";

const ESCAPED_BR = /&lt;br\s*\/?\s*&gt;/gi;

type LitUpdated = (changed: Map<PropertyKey, unknown>) => void;

const proto = MarkdownBlock.prototype as unknown as { updated?: LitUpdated };
const originalUpdated = proto.updated;
proto.updated = function (this: HTMLElement, changed: Map<PropertyKey, unknown>) {
	originalUpdated?.call(this, changed);
	try {
		fixEscapedBrInTables(this);
		addTableCopyButtons(this);
	} catch (err) {
		console.warn("[markdown-fixups]", err);
	}
};

function fixEscapedBrInTables(root: HTMLElement) {
	for (const cell of root.querySelectorAll("td, th")) {
		const html = cell.innerHTML;
		if (ESCAPED_BR.test(html)) {
			cell.innerHTML = html.replace(ESCAPED_BR, "<br>");
		}
		// .test() with a /g/ regex is stateful — reset for the next cell.
		ESCAPED_BR.lastIndex = 0;
	}
}

function addTableCopyButtons(root: HTMLElement) {
	// MarkdownBlock wraps every table in <div class="overflow-x-auto …">.
	for (const table of root.querySelectorAll("table")) {
		const wrapper = table.parentElement;
		if (!wrapper || wrapper.dataset.copyEnhanced === "1") continue;
		wrapper.dataset.copyEnhanced = "1";
		wrapper.classList.add("md-table-wrapper");

		const btn = document.createElement("button");
		btn.type = "button";
		btn.className = "md-table-copy";
		btn.title = "Tabelle kopieren (fügt in Word/Excel als echte Tabelle ein)";
		btn.textContent = "⧉ Copy";
		btn.addEventListener("click", () => {
			void copyTable(table).then((ok) => {
				btn.textContent = ok ? "✓ Kopiert" : "✗ Fehler";
				setTimeout(() => (btn.textContent = "⧉ Copy"), 1500);
			});
		});
		wrapper.appendChild(btn);
	}
}

async function copyTable(table: HTMLTableElement): Promise<boolean> {
	const clone = table.cloneNode(true) as HTMLTableElement;
	// Inline styles — Word/Outlook ignore external CSS on paste, so borders
	// and padding must live on the elements themselves.
	clone.style.borderCollapse = "collapse";
	clone.removeAttribute("class");
	for (const cell of clone.querySelectorAll("th, td")) {
		const el = cell as HTMLElement;
		el.removeAttribute("class");
		el.style.border = "1px solid #999";
		el.style.padding = "4px 8px";
		el.style.textAlign = "left";
		el.style.verticalAlign = "top";
		if (el.tagName === "TH") el.style.background = "#efefef";
	}

	const htmlPayload = `<meta charset="utf-8">${clone.outerHTML}`;
	const textPayload = Array.from(table.rows)
		.map((row) =>
			Array.from(row.cells)
				.map((c) => (c.textContent ?? "").replace(/\s+/g, " ").trim())
				.join("\t"),
		)
		.join("\n");

	try {
		await navigator.clipboard.write([
			new ClipboardItem({
				"text/html": new Blob([htmlPayload], { type: "text/html" }),
				"text/plain": new Blob([textPayload], { type: "text/plain" }),
			}),
		]);
		return true;
	} catch {
		// Older browsers / non-secure contexts: select the live table and use
		// the legacy copy command, which also carries rich HTML.
		try {
			const range = document.createRange();
			range.selectNode(table);
			const sel = window.getSelection();
			sel?.removeAllRanges();
			sel?.addRange(range);
			const ok = document.execCommand("copy");
			sel?.removeAllRanges();
			return ok;
		} catch {
			return false;
		}
	}
}
